import { useRef } from 'react';
import { Icon, spinnerGapIcon } from '../core/icons';
import { usePresence } from '../motion/presence';
import type { ImportStage } from './importFiles';
import './import.css';

export interface ImportProgress {
  name: string;
  /** Zero-based, of `count`. */
  index: number;
  count: number;
  stage: ImportStage;
}

/** What is happening, in a few words. The file's name is the thing the person is waiting on. */
export function importLabel(p: ImportProgress): string {
  const of = p.count > 1 ? ` (${p.index + 1} of ${p.count})` : '';
  switch (p.stage) {
    case 'pictures':
      return `Adding pictures to ${p.name}${of}`;
    case 'comments':
      return `Adding comments to ${p.name}${of}`;
    default:
      return `Reading ${p.name}${of}`;
  }
}

/**
 * A line at the foot of the page while a file is being brought in. It rises in and fades out
 * with the toasts' motion, and holds the last thing it said while it leaves.
 */
export function ImportStatus({ progress }: { progress: ImportProgress | null }) {
  const { present, state } = usePresence(progress !== null);
  const last = useRef<ImportProgress | null>(null);
  if (progress) last.current = progress;
  const shown = progress ?? last.current;
  if (!present || !shown) return null;
  return (
    <div className="mv-import mv-rise" data-state={state} role="status" aria-live="polite">
      <span className="mv-spin mv-import-spin" aria-hidden="true">
        <Icon icon={spinnerGapIcon} size={16} />
      </span>
      <span className="mv-import-text">{importLabel(shown)}</span>
    </div>
  );
}
